/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './ContractBuilder.style';
import { useIntl } from 'react-intl';
import { Button, Col, message, Row, Spin, Typography } from 'antd';
import { Fragment, useEffect, useState } from 'react';
import IconButton from '~components/_common/IconButton/IconButton';
import ContractBuilderGroup from './ContractBuilderGroup/ContractBuilderGroup';
import UnitSumCard from './ContractBuilderGroupCard/CardTypes/UnitSumType/UnitSumCard';
import TermOfServiceCard from './ContractBuilderGroupCard/CardTypes/TermOfServiceType/TermOfServiceCard';
import OfferListCard from './ContractBuilderGroupCard/CardTypes/OfferListType/OfferListCard';
import UploadCertifiedDocumentCard from './ContractBuilderGroupCard/CardTypes/UploadCertifiedDocumentType/UploadCertifiedDocumentCard';
import { ContractBuilderAddGroupModal } from '~components/popups-modals/ContractBuilderModals/ContractBuilderAddGroupModal/ContractBuilderAddGroupModal';
import { ContractBuilderAddCardModal } from '~components/popups-modals/ContractBuilderModals/ContractBuilderAddCardModal/ContractBuilderAddCardModal';
import { DataContextMenuItemType } from '~src/data/dataContextMenu';
import { UniversalDropSpacer } from './ContractBuilderDnD/UniversalDropSpacer';
import { queryCache, useMutation, useQuery } from 'react-query';
import {
  assignVoucherToGroup,
  createVoucher,
  createVoucherGroup,
  deleteVoucher,
  deleteVoucherGroup,
  getVouchers,
  reactivateVoucher,
  reactivateVoucherGroup,
  shareTenderContractsVouchers,
  updateVoucherOrder,
} from '~src/services/api/contracts';
import { SimpleConfirmModal } from '~components/popups-modals/ConfirmModal/SimpleConfirmModal';
import { ContractBuilderEditModal } from '~components/popups-modals/ContractBuilderModals/ContractBuilderEditModal/ContractBuilderEdit.modal';
import {
  actionOffer,
  getVoucherResponses,
  releaseOffer,
} from '~src/services/api/shares';

type ContractBuilderProps = {
  projectId?: number;
  tenderId?: string;
  contractId?: any;
  shareId?: string;
  shareKey?: string;
  isOnMyTender?: boolean;
  contractOfferData: {
    is_offer_created: boolean;
    offer_id: number;
    state: number;
    contract_id: number;
  };
  tenderClosed?: boolean;
  showDeleted?: boolean;
  contractDeleted?: boolean;
};

type DbVoucher = {
  id: number;
  name: string;
  description?: string;
  voucher_type: 1 | 2 | 3 | 4;
  unit_count?: number;
  unit_type?: number;
  created_at?: number;
  is_voucher_group: boolean;
  voucher_group_option?: {
    voucher_group_contract_id?: number;
    sub_voucher_ids: number[];
  };
  attachments?: any[];
  sort?: number;
  tender_voucher_id?: number;
  state?: number;

  // TODO Optional Voucher API:
  is_optional?: boolean;
};

type DbVoucherGroupOrCard = DbVoucher;
type ContractEntry = DbVoucherGroupOrCard;

const ContractBuilder: React.FC<ContractBuilderProps> = ({
  projectId,
  tenderId,
  contractId,
  shareId,
  shareKey,
  isOnMyTender,
  contractOfferData,
  tenderClosed,
  showDeleted,
  contractDeleted,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const [contracts, setContracts] = useState<ContractEntry[]>([]);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showConfirmDeleteCardModal, setShowConfirmDeleteCardModal] = useState(
    false
  );
  const [
    showConfirmDeleteGroupModal,
    setShowConfirmDeleteGroupModal,
  ] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    id: number;
    name: string;
    type: 'group' | 'card';
  } | null>(null);
  const [itemToEdit, setItemToEdit] = useState<{
    data: DbVoucherGroupOrCard;
    type: 'group' | 'card';
  } | null>(null);
  const [rightClickGroupIndex, setRightClickGroupIndex] = useState<
    number | null
  >(null);
  const [rightClickCardInfo, setRightClickCardInfo] = useState<{
    cardId: number | null;
    groupVisualIndex: number | null;
  } | null>(null);
  const [draggingGroupId, setDraggingGroupId] = useState<string | null>(null);
  const [draggingCardId, setDraggingCardId] = useState<string | null>(null);
  const [showConfirmFinalizeModal, setShowConfirmFinalizeModal] = useState(
    false
  );
  const [showConfirmReopenModal, setShowConfirmReopenModal] = useState(false);

  // Utility functions
  // Checks if an entry is a group
  const isGroup = (entry: DbVoucherGroupOrCard): boolean =>
    entry.is_voucher_group === true;

  // React Query Setup
  const queryKey = [projectId, tenderId, contractId, showDeleted];

  // Gets current data from query cache
  const getCurrentData = () =>
    queryCache.getQueryData<ContractEntry[]>(queryKey);

  // Returns visible root items (groups and cards not in groups)
  const getVisibleRootItems = (
    data: ContractEntry[] | undefined
  ): ContractEntry[] => {
    if (!data) return [];
    const currentGroupedCardIds = new Set(
      data
        .filter((c) => c.is_voucher_group)
        .flatMap((g) => g.voucher_group_option?.sub_voucher_ids || [])
    );
    return data
      .filter((entry) =>
        entry.is_voucher_group ? true : !currentGroupedCardIds.has(entry.id)
      )
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
  };

  // Performs optimistic update for better UX
  const performOptimisticUpdate = (
    updateFn: (currentData: ContractEntry[]) => ContractEntry[]
  ): { previousContracts: ContractEntry[] | undefined } => {
    const previousContracts = getCurrentData();
    if (previousContracts) {
      const currentDeepCopy = JSON.parse(
        JSON.stringify(previousContracts)
      ) as ContractEntry[];
      const optimisticContracts = updateFn(currentDeepCopy);
      queryCache.setQueryData(queryKey, optimisticContracts);
    }
    return { previousContracts };
  };

  // Mutations
  // Add voucher mutation
  const [addVoucher, { isLoading: isAddingCard }] = useMutation(createVoucher, {
    onSuccess: () => {
      queryCache.invalidateQueries(queryKey);
      // Also invalidate the parent contracts list (VoucherBuilder.tsx's
      // useContractsUnified) - its has_tender_voucher flag drives the
      // empty-contract warning styling on this contract's row header, and
      // wouldn't otherwise refresh until an unrelated refetch happened.
      queryCache.invalidateQueries([
        'contracts',
        projectId,
        tenderId,
        shareId,
        shareKey,
        showDeleted,
      ]);
      setShowAddCardModal(false);
    },
    onError: (err) => console.error('Add Voucher Error:', err),
  });

  // Delete voucher mutation
  const [doDeleteVoucher, { isLoading: isDeletingCard }] = useMutation(
    deleteVoucher,
    {
      onMutate: async ({ voucherId }) => {
        await queryCache.cancelQueries(queryKey);
        const { previousContracts } = performOptimisticUpdate((current) => {
          const updated = current.filter((v) => v.id !== voucherId);
          updated.forEach((group) => {
            if (
              group.is_voucher_group &&
              group.voucher_group_option?.sub_voucher_ids
            ) {
              const index = group.voucher_group_option.sub_voucher_ids.indexOf(
                voucherId
              );
              if (index !== -1)
                group.voucher_group_option.sub_voucher_ids.splice(index, 1);
            }
          });
          return updated;
        });
        return { previousContracts };
      },
      onSuccess: () => {
        queryCache.invalidateQueries(queryKey);
        // Also invalidate the parent contracts list - deleting the last
        // voucher on a contract should bring the empty-contract warning
        // styling back without waiting for an unrelated refetch.
        queryCache.invalidateQueries([
          'contracts',
          projectId,
          tenderId,
          shareId,
          shareKey,
          showDeleted,
        ]);
        setItemToDelete(null);
        setShowConfirmDeleteCardModal(false);
      },
      onError: (err, variables, context) => {
        console.error('Delete Voucher Error:', err);
        message.error(intl.formatMessage({ id: 'failed-delete-card' }));
        if (context?.previousContracts)
          queryCache.setQueryData(queryKey, context.previousContracts);
        setItemToDelete(null);
        setShowConfirmDeleteCardModal(false);
      },
    }
  );

  const [doReactivateVoucher] = useMutation(reactivateVoucher, {
    onSuccess: () => {
      queryCache.invalidateQueries(queryKey);
      message.success(
        intl.formatMessage({ id: 'voucher-reactivated-success' })
      );
    },
    onError: (err) => {
      console.error('Reactivate Voucher Error:', err);
      message.error(intl.formatMessage({ id: 'failed-reactivate-card' }));
    },
  });

  const [doReactivateGroup] = useMutation(reactivateVoucherGroup, {
    onSuccess: () => {
      queryCache.invalidateQueries(queryKey);
      message.success(intl.formatMessage({ id: 'group-reactivated-success' }));
    },
    onError: (err) => {
      console.error('Reactivate Group Error:', err);
      message.error(intl.formatMessage({ id: 'failed-reactivate-group' }));
    },
  });

  // Add group mutation
  const [doAddGroup, { isLoading: isAddingGroup }] = useMutation(
    createVoucherGroup,
    {
      onSuccess: () => {
        queryCache.invalidateQueries(queryKey);
        setShowAddGroupModal(false);
      },
      onError: (err) => console.error('Add Group Error:', err),
    }
  );

  // Delete group mutation
  const [doDeleteGroup, { isLoading: isDeletingGroup }] = useMutation(
    deleteVoucherGroup,
    {
      onSuccess: () => {
        queryCache.invalidateQueries(queryKey);
        setItemToDelete(null);
        setShowConfirmDeleteGroupModal(false);
      },
      onError: (err) => {
        console.error('Delete Group Error:', err);
        setItemToDelete(null);
        setShowConfirmDeleteGroupModal(false);
      },
    }
  );

  // Assign/Move Card Mutation - Handles moving cards between groups or to/from root
  const [doAssignVoucherToGroup] = useMutation(assignVoucherToGroup, {
    onMutate: async (variables) => {
      await queryCache.cancelQueries(queryKey);
      const { voucherId, voucher_group_id, sort } = variables;

      const { previousContracts } = performOptimisticUpdate((current) => {
        const updatedMap = new Map(
          current.map((item) => [item.id, { ...item }])
        );
        const draggedCard = updatedMap.get(voucherId);
        if (!draggedCard) return undefined;

        // Remove card from its current position
        updatedMap.forEach((group) => {
          if (
            group.is_voucher_group &&
            group.voucher_group_option?.sub_voucher_ids
          ) {
            const subIds = group.voucher_group_option.sub_voucher_ids;
            const idx = subIds.indexOf(voucherId);
            if (idx !== -1) {
              group.voucher_group_option.sub_voucher_ids.splice(idx, 1);
            }
          }
        });

        // Add card to the new position (if moving TO a group)
        if (voucher_group_id !== undefined) {
          const targetGroup = updatedMap.get(voucher_group_id);
          if (targetGroup?.is_voucher_group) {
            if (!targetGroup.voucher_group_option)
              targetGroup.voucher_group_option = { sub_voucher_ids: [] };
            if (!targetGroup.voucher_group_option.sub_voucher_ids)
              targetGroup.voucher_group_option.sub_voucher_ids = [];

            // Add the card to the correct position in the group
            targetGroup.voucher_group_option.sub_voucher_ids.splice(
              sort,
              0,
              voucherId
            );
          } else {
            console.error('Target group not found', voucher_group_id);
          }
        } else {
          // Moving TO ROOT
          draggedCard.sort = sort;
        }

        return Array.from(updatedMap.values());
      });
      return { previousContracts };
    },
    onError: (err, variables, context) => {
      console.error('Failed to assign/move voucher', err);
      message.error(intl.formatMessage({ id: 'failed-move-card' }));
      if (context?.previousContracts)
        queryCache.setQueryData(queryKey, context.previousContracts);
    },
    onSettled: () => {
      queryCache.invalidateQueries(queryKey);
    },
  });

  // Reorder Items Mutation - Handles reordering cards and groups
  const [doReorderVouchers] = useMutation(updateVoucherOrder, {
    onMutate: async (variables) => {
      await queryCache.cancelQueries(queryKey);
      const { order } = variables;
      const { previousContracts } = performOptimisticUpdate((current) => {
        const updated = [...current];
        const orderMap = new Map(
          order.map((item) => [item.voucher_id, item.order_flag])
        );

        // Determine if this is an intra-group reorder
        let parentGroup = null;
        let isIntraGroupReorder = false;

        if (order.length > 0) {
          const firstCardId = order[0].voucher_id;

          // Find the parent group containing the first card
          parentGroup = updated.find(
            (g) =>
              g.is_voucher_group &&
              g.voucher_group_option?.sub_voucher_ids.includes(firstCardId)
          );

          // Check if all cards in the order payload belong to this group
          if (parentGroup?.voucher_group_option) {
            isIntraGroupReorder = order.every((orderItem) =>
              parentGroup.voucher_group_option.sub_voucher_ids.includes(
                orderItem.voucher_id
              )
            );
          }
        }

        if (isIntraGroupReorder && parentGroup) {
          // For intra-group reordering
          const allGroupCardIds = [
            ...parentGroup.voucher_group_option.sub_voucher_ids,
          ];

          // Remove cards in the order payload from current group
          const cardsToMove = order.map((item) => item.voucher_id);
          const remainingCards = allGroupCardIds.filter(
            (id) => !cardsToMove.includes(id)
          );

          // Create a map of card IDs to their desired positions
          const cardPositionMap = new Map(
            order.map((item) => [item.voucher_id, item.order_flag])
          );

          // Create a new array with all card IDs
          const allCardIds = [
            ...order.map((item) => item.voucher_id),
            ...remainingCards,
          ];

          // Sort the array based on the position map or original position
          const newSubVoucherIds = allCardIds.sort((a, b) => {
            if (cardPositionMap.has(a) && cardPositionMap.has(b)) {
              return cardPositionMap.get(a) - cardPositionMap.get(b);
            }
            if (cardPositionMap.has(a)) {
              return -1;
            }
            if (cardPositionMap.has(b)) {
              return 1;
            }
            return allGroupCardIds.indexOf(a) - allGroupCardIds.indexOf(b);
          });

          // Update the group's sub_voucher_ids with the new order
          parentGroup.voucher_group_option.sub_voucher_ids = newSubVoucherIds;

          // Update the sort values for all cards in the group
          newSubVoucherIds.forEach((cardId, index) => {
            const card = updated.find(
              (item) => item.id === cardId && !item.is_voucher_group
            );
            if (card) {
              card.sort = index;
            }
          });
        } else {
          // For root items or mixed reordering
          updated.forEach((item) => {
            if (orderMap.has(item.id)) {
              item.sort = orderMap.get(item.id);
            }
          });
        }

        return updated;
      });
      return { previousContracts };
    },
    onError: (error, variables, context) => {
      console.error('Failed to reorder vouchers', error);
      message.error(intl.formatMessage({ id: 'failed-reorder-items' }));
      if (context?.previousContracts)
        queryCache.setQueryData(queryKey, context.previousContracts);
    },
    onSettled: () => {
      queryCache.invalidateQueries(queryKey);
    },
  });

  // Event Handlers
  // Handles adding a new card/voucher
  const handleAddCard = async (newCardData) => {
    try {
      const currentData = getCurrentData() ?? [];
      const groupId =
        rightClickGroupIndex !== null
          ? getVisibleRootItems(currentData)[rightClickGroupIndex]?.id
          : undefined;
      let sort = 0;
      if (groupId !== undefined) {
        const group = currentData.find((e) => e.id === groupId);
        sort = group?.voucher_group_option?.sub_voucher_ids?.length ?? 0;
      } else {
        sort = getVisibleRootItems(currentData).length;
      }
      await addVoucher({
        projectId,
        tenderId: Number(tenderId),
        contractId,
        data: {
          name: newCardData.cardTitle,
          description: newCardData.cardSubtitle,
          voucher_type: newCardData.type,
          unit_count: newCardData.cardQuantity,
          unit_type: newCardData.unitType,
          sort,
          voucher_group_id: groupId,
          is_optional: newCardData.isOptional,
        },
      });
      setRightClickGroupIndex(null);
    } catch (err) {
      console.error('Failed to create voucher', err);
      message.error(intl.formatMessage({ id: 'failed-add-voucher' }));
    }
  };

  // Handles adding a new group
  const handleAddGroup = async (newGroupData) => {
    try {
      const currentData = getCurrentData() ?? [];
      const sort = getVisibleRootItems(currentData).length;
      await doAddGroup({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        contractId: Number(contractId),
        data: {
          name: newGroupData.name,
          description: newGroupData.description,
          sort: sort,
          is_ask_price: false,
        },
      });
    } catch (err) {
      console.error('Failed to create group', err);
      message.error(intl.formatMessage({ id: 'failed-add-group' }));
    }
  };

  // Handles context menu actions for groups (delete, add card, edit)
  const handleGroupContextMenuClick = async (
    item: DataContextMenuItemType,
    originalIndex?: number
  ) => {
    if (typeof originalIndex !== 'number') return;
    const currentData = getCurrentData();
    if (!currentData) return;
    const group = currentData[originalIndex];

    if (item.modal === 'reactivate-group' && group && isGroup(group)) {
      if (contractDeleted) return;

      await doReactivateGroup({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        contractId: Number(contractId),
        voucherId: group.id,
      });
      return;
    }

    if (item.modal === 'delete' && group && isGroup(group)) {
      if (
        group.voucher_group_option?.sub_voucher_ids &&
        group.voucher_group_option.sub_voucher_ids.length > 0
      ) {
        message.error(intl.formatMessage({ id: 'group-must-be-empty' }));
        return;
      }
      if (!group.id) {
        message.error(
          intl.formatMessage({ id: 'cannot-delete-group-missing-id' })
        );
        return;
      }

      setItemToDelete({ id: group.id, name: group.name, type: 'group' });
      setShowConfirmDeleteGroupModal(true);
    } else if (item.modal === 'add-card') {
      if (group.state === 9 || contractDeleted) return;
      const visualIndex = getVisibleRootItems(currentData).findIndex(
        (g) => g.id === group.id
      );
      setRightClickGroupIndex(visualIndex);
      setShowAddCardModal(true);
    } else if (item.modal === 'edit-group') {
      if (contractDeleted) return;
      setItemToEdit({ data: group, type: 'group' });
    }
  };

  // Handles right-click on a card to show context menu
  const handleRightClickCard = (
    e: React.MouseEvent,
    cardId: number | null,
    groupVisualIndex: number | null
  ) => {
    e.preventDefault();
    if (cardId === null) return;
    setRightClickCardInfo({ cardId, groupVisualIndex });
  };

  // Handles context menu actions for cards (delete, edit)
  const handleCardContextMenuClick = (item: DataContextMenuItemType) => {
    if (!rightClickCardInfo || rightClickCardInfo.cardId === null) {
      return;
    }

    const cardId = Number(rightClickCardInfo.cardId);
    const currentData = getCurrentData();
    if (!currentData) return;

    if (item.modal === 'reactivate-card') {
      if (contractDeleted) {
        setRightClickCardInfo(null);
        return;
      }

      const card = currentData.find(
        (c) => c.id === cardId && !c.is_voucher_group
      );
      if (!card) return;

      doReactivateVoucher({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        contractId: Number(contractId),
        voucherId: cardId,
      });
      setRightClickCardInfo(null);
      return;
    }

    if (item.modal === 'delete') {
      if (contractDeleted) {
        setRightClickCardInfo(null);
        return;
      }

      if (!projectId || !tenderId || !contractId) {
        message.error(intl.formatMessage({ id: 'missing-required-ids' }));
        return;
      }

      const cardToDelete = currentData.find(
        (c) => c.id === cardId && !c.is_voucher_group
      );

      if (!cardToDelete) {
        message.error(intl.formatMessage({ id: 'card-details-not-found' }));
        setRightClickCardInfo(null);
        return;
      }

      setItemToDelete({ id: cardId, name: cardToDelete.name, type: 'card' });
      setShowConfirmDeleteCardModal(true);
      setRightClickCardInfo(null);
    } else if (item.modal === 'edit-card') {
      if (contractDeleted) {
        setRightClickCardInfo(null);
        return;
      }

      const card = currentData.find(
        (c) => c.id === cardId && !c.is_voucher_group
      );
      if (!card) return;

      setItemToEdit({
        data: { ...card, sort: card.sort },
        type: 'card',
      });
      setRightClickCardInfo(null);
    }
  };

  // Drag & Drop Handlers
  // Handles dropping a card to a root position
  const handleCardDrop = async (
    cardId: string,
    targetRootVisualIndex: number
  ) => {
    setDraggingCardId(null);
    const currentData = getCurrentData();
    if (!currentData) return;
    const cardNumericId = Number(cardId);
    const draggedCard = currentData.find((c) => c.id === cardNumericId);
    if (!draggedCard) return;

    const isMoveFromGroup = currentData.some(
      (group) =>
        group.is_voucher_group &&
        group.voucher_group_option?.sub_voucher_ids?.includes(cardNumericId)
    );

    if (isMoveFromGroup) {
      // Move card FROM Group TO Root (Two API Call Approach)
      const { previousContracts } = performOptimisticUpdate((current) => {
        const updatedMap = new Map(
          current.map((item) => [item.id, { ...item }])
        );
        const cardToMove = updatedMap.get(cardNumericId);
        if (!cardToMove) {
          return undefined;
        }

        // Remove from old group
        let removed = false;
        updatedMap.forEach((group) => {
          if (
            !removed &&
            group.is_voucher_group &&
            group.voucher_group_option?.sub_voucher_ids
          ) {
            const subIds = group.voucher_group_option.sub_voucher_ids;
            const idx = subIds.indexOf(cardNumericId);
            if (idx !== -1) {
              group.voucher_group_option.sub_voucher_ids.splice(idx, 1);
              removed = true;
            }
          }
        });

        // Prepare for root insertion
        const currentItemsArray = Array.from(updatedMap.values());
        // Get visible root items excluding the dragged card
        const currentVisibleRoot = getVisibleRootItems(
          currentItemsArray
        ).filter((item) => item.id !== cardNumericId);

        // Insert the card at the target position
        const targetSpliceIndex = Math.max(
          0,
          Math.min(targetRootVisualIndex, currentVisibleRoot.length)
        );
        currentVisibleRoot.splice(targetSpliceIndex, 0, cardToMove);

        // Update sort properties
        currentVisibleRoot.forEach((item, index) => {
          const itemInMap = updatedMap.get(item.id);
          if (itemInMap) itemInMap.sort = index;
        });

        return Array.from(updatedMap.values());
      });

      try {
        // Call Assign to handle group membership change
        await doAssignVoucherToGroup({
          projectId: Number(projectId),
          tenderId: Number(tenderId),
          contractId: Number(contractId),
          voucherId: cardNumericId,
          voucher_group_id: undefined, // Assign to root
          sort: targetRootVisualIndex,
        });

        // Get optimistic data for reordering
        const optimisticData = queryCache.getQueryData<ContractEntry[]>(
          queryKey
        );
        if (!optimisticData) {
          queryCache.invalidateQueries(queryKey);
          return;
        }

        // Calculate payload based on optimistic update
        const finalVisibleRoot = getVisibleRootItems(optimisticData);
        const orderPayload = finalVisibleRoot.map((item, index) => ({
          voucher_id: Number(item.id),
          order_flag: index,
        }));

        // Call Reorder to enforce the correct final order
        await doReorderVouchers({
          projectId: Number(projectId),
          tenderId: Number(tenderId),
          contractId: Number(contractId),
          order: orderPayload,
        });
      } catch (error) {
        console.error('Error during group-to-root move:', error);
        if (previousContracts) {
          queryCache.setQueryData(queryKey, previousContracts);
        }
        message.error('Failed to move card. Reverting.');
      } finally {
        queryCache.invalidateQueries(queryKey);
      }
    } else {
      // Reorder card WITHIN Root
      const visibleRootItems = getVisibleRootItems(currentData);
      const currentRootIndex = visibleRootItems.findIndex(
        (item) => item.id === cardNumericId
      );
      if (currentRootIndex === -1) {
        return;
      }

      const tempOrderedList = [...visibleRootItems];
      const [movedItem] = tempOrderedList.splice(currentRootIndex, 1);
      const insertionIndex =
        currentRootIndex < targetRootVisualIndex
          ? targetRootVisualIndex - 1
          : targetRootVisualIndex;
      // Ensure insertion index is valid
      const finalInsertionIndex = Math.max(
        0,
        Math.min(insertionIndex, tempOrderedList.length)
      );
      tempOrderedList.splice(finalInsertionIndex, 0, movedItem);

      const orderPayload = tempOrderedList.map((item, index) => ({
        voucher_id: Number(item.id),
        order_flag: index,
      }));

      // Call the mutation
      await doReorderVouchers({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        contractId: Number(contractId),
        order: orderPayload,
      });
    }
  };

  // Handles dropping a card into a group
  const handleDropCardToGroup = async (
    cardId: string,
    targetGroupVisualIndex: number,
    targetCardIndexInGroup: number
  ) => {
    setDraggingCardId(null);
    const currentData = getCurrentData();
    if (!currentData) return;

    const visibleRootItems = getVisibleRootItems(currentData);
    const targetGroup = visibleRootItems[targetGroupVisualIndex];

    if (!targetGroup || !targetGroup.is_voucher_group)
      return message.error(
        intl.formatMessage({ id: 'target-group-not-found' })
      );

    const cardNumericId = Number(cardId);

    const isCardAlreadyInGroup = targetGroup.voucher_group_option?.sub_voucher_ids?.includes(
      cardNumericId
    );

    if (isCardAlreadyInGroup) {
      // If card is already in the group, just reorder it
      await handleReorderCardsInGroup(
        targetGroupVisualIndex,
        cardNumericId,
        targetCardIndexInGroup
      );
    } else {
      try {
        // Assign card to group
        await doAssignVoucherToGroup({
          projectId: Number(projectId),
          tenderId: Number(tenderId),
          contractId: Number(contractId),
          voucherId: cardNumericId,
          voucher_group_id: Number(targetGroup.id),
          sort: targetCardIndexInGroup,
        });

        // Get updated data
        const updatedData = getCurrentData();
        if (!updatedData) return;

        const updatedVisibleRootItems = getVisibleRootItems(updatedData);
        const updatedTargetGroup =
          updatedVisibleRootItems[targetGroupVisualIndex];

        if (
          !updatedTargetGroup?.is_voucher_group ||
          !updatedTargetGroup.voucher_group_option
        ) {
          // return message.error('Could not find the updated target group data.');
        }

        // Rebuild list of cards in group
        const allCardsInGroup = updatedData
          .filter(
            (c) =>
              !c.is_voucher_group &&
              updatedTargetGroup.voucher_group_option?.sub_voucher_ids.includes(
                c.id
              )
          )
          .sort((a, b) => a.sort! - b.sort!);

        const draggedCard = updatedData.find((c) => c.id === cardNumericId);
        if (!draggedCard) return;

        const reordered = [...allCardsInGroup];
        // Remove if already exists (prevent duplicates)
        const existingIndex = reordered.findIndex(
          (c) => c.id === cardNumericId
        );
        if (existingIndex !== -1) reordered.splice(existingIndex, 1);
        // Insert in new position
        reordered.splice(targetCardIndexInGroup, 0, draggedCard);

        // Create payload with updated order
        const orderPayload = reordered.map((card, index) => ({
          voucher_id: card.id,
          order_flag: index,
        }));

        // Submit reorder
        if (orderPayload.length > 0) {
          await doReorderVouchers({
            projectId: Number(projectId),
            tenderId: Number(tenderId),
            contractId: Number(contractId),
            order: orderPayload,
          });
        }
      } catch (error) {
        console.error('Error in handleDropCardToGroup:', error);
        message.error(intl.formatMessage({ id: 'failed-assign-card' }));
      }
    }
  };

  // Handles dropping a group to reorder it
  const handleGroupDrop = async (
    groupId: string,
    targetRootVisualIndex: number
  ) => {
    setDraggingGroupId(null);
    const currentData = getCurrentData();
    if (!currentData) return;
    const visibleRootItems = getVisibleRootItems(currentData);
    const fromIndex = visibleRootItems.findIndex(
      (g) => g.id.toString() === groupId
    );
    if (fromIndex === -1) return;

    const tempOrderedList = [...visibleRootItems];
    const [groupToMove] = tempOrderedList.splice(fromIndex, 1);
    // Adjust insertion index based on direction of movement
    const insertionIndex =
      fromIndex < targetRootVisualIndex
        ? targetRootVisualIndex - 1
        : targetRootVisualIndex;
    tempOrderedList.splice(insertionIndex, 0, groupToMove);

    const orderPayload = tempOrderedList.map((item, index) => ({
      voucher_id: Number(item.id),
      order_flag: index,
    }));

    await doReorderVouchers({
      projectId: Number(projectId),
      tenderId: Number(tenderId),
      contractId: Number(contractId),
      order: orderPayload,
    });
  };

  // Handles reordering cards within a group
  const handleReorderCardsInGroup = async (
    groupVisualIndex: number,
    cardIdBeingMoved: number,
    newIndexInGroup: number
  ) => {
    setDraggingCardId(null);
    const currentData = getCurrentData();
    if (!currentData) return;
    const visibleRootItems = getVisibleRootItems(currentData);
    const targetGroup = visibleRootItems[groupVisualIndex];
    if (!targetGroup?.is_voucher_group || !targetGroup.voucher_group_option) {
      return message.error(intl.formatMessage({ id: 'group-data-not-found' }));
    }

    // Get actual cards in the group in their current order
    const actualGroupCards = currentData
      .filter(
        (c) =>
          !c.is_voucher_group &&
          targetGroup.voucher_group_option?.sub_voucher_ids.includes(c.id)
      )
      .sort((a, b) => a.sort! - b.sort!);

    const currentSubIds = actualGroupCards.map((c) => c.id);
    const fromIndexInGroup = currentSubIds.indexOf(cardIdBeingMoved);

    if (fromIndexInGroup === -1) {
      return message.error(
        intl.formatMessage({ id: 'card-not-found-in-group' })
      );
    }

    // Create new ordered list
    const newOrderedSubIds = [...currentSubIds];
    newOrderedSubIds.splice(fromIndexInGroup, 1);
    newOrderedSubIds.splice(newIndexInGroup, 0, cardIdBeingMoved);

    const orderPayload = newOrderedSubIds.map((cardId, index) => ({
      voucher_id: cardId,
      order_flag: index,
    }));
    try {
      // Update the order of cards
      await doReorderVouchers({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        contractId: Number(contractId),
        order: orderPayload,
      });
    } catch (error) {
      console.error('Error in handleReorderCardsInGroup:', error);
      message.error(intl.formatMessage({ id: 'failed-reorder-cards' }));

      // Rollback optimistic update on error
      if (previousContracts) {
        queryCache.setQueryData(queryKey, previousContracts);
      }
    }
  };

  // Data Fetching and State Sync
  const { data: queryData, isLoading, isError } = useQuery<ContractEntry[]>(
    queryKey,
    () => {
      if (isOnMyTender) {
        return shareTenderContractsVouchers({
          share_id: shareId!,
          Share_Key: shareKey!,
          contracts_id: contractId,
        });
      }
      return getVouchers({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        contractId,
        show_deleted: showDeleted,
      });
    },
    {
      enabled:
        !!contractId &&
        (isOnMyTender ? !!shareId && !!shareKey : !!projectId && !!tenderId),
      keepPreviousData: true,
    }
  );

  const { data: voucherResponses } = useQuery(
    ['voucher-responses', shareId, shareKey, contractOfferData?.offer_id],
    () =>
      getVoucherResponses({
        share_id: shareId!,
        share_key: shareKey!,
        offer_id: contractOfferData!.offer_id,
      }),
    {
      enabled:
        isOnMyTender &&
        !!shareId &&
        !!shareKey &&
        !!contractOfferData?.is_offer_created &&
        !!contractOfferData?.offer_id,
    }
  );

  const [doReleaseOffer, { isLoading: isReleasing }] = useMutation(
    actionOffer,
    {
      onSuccess: () => {
        message.success(intl.formatMessage({ id: 'offer-finalized-success' }));
        queryCache.invalidateQueries([
          'voucher-responses',
          shareId,
          shareKey,
          contractOfferData?.offer_id,
        ]);
        queryCache.invalidateQueries([
          'contracts',
          projectId,
          tenderId,
          shareId,
          shareKey,
        ]);
        setShowConfirmFinalizeModal(false);
      },
      onError: (err) => {
        console.error('Failed to finalize offer:', err);
        message.error(intl.formatMessage({ id: 'offer-finalize-failed' }));
      },
    }
  );

  const [doReopenOffer, { isLoading: isReopening }] = useMutation(actionOffer, {
    onSuccess: () => {
      message.success(intl.formatMessage({ id: 'offer-reopened-success' }));
      queryCache.invalidateQueries([
        'contracts',
        projectId,
        tenderId,
        shareId,
        shareKey,
      ]);
      setShowConfirmReopenModal(false);
    },
    onError: (err) => {
      console.error('Failed to reopen offer:', err);
      message.error(intl.formatMessage({ id: 'offer-reopen-failed' }));
    },
  });

  // Offer finalize logic
  const requiredVouchers = (queryData ?? []).filter(
    (c) => !c.is_voucher_group && !c.is_optional
  );

  const hasAllRequiredResponses = requiredVouchers.every((voucher) => {
    const response = voucherResponses?.find((r) => r.voucher_id === voucher.id);

    if (!response) {
      return false; // No response at all
    }

    // For Offer List (type 2) and Upload Certified Document (type 3),
    // check if attachments exist and have content
    if (voucher.voucher_type === 2 || voucher.voucher_type === 3) {
      return (
        response.attachments &&
        response.attachments.length > 0 &&
        response.attachments.some((att: any) => att.id || att.file)
      );
    }

    // For other voucher types, just check if response exists
    return true;
  });

  const isOfferLocked =
    contractOfferData?.state === 4 || // Finalized
    contractOfferData?.state === 5; // Rejected

  const isOfferEditable =
    contractOfferData?.state === 1 || // Awaiting
    contractOfferData?.state === 3; // Reopen

  const isOfferFinalizable =
    contractOfferData?.is_offer_created &&
    isOfferEditable &&
    hasAllRequiredResponses;

  // Contracts setup
  useEffect(() => {
    if (queryData) setContracts(queryData);
  }, [queryData]);

  // Render Logic
  if (isLoading && !queryData && isError)
    return <Spin tip={intl.formatMessage({ id: 'loading-contract' })} />;

  const itemsToRender = getVisibleRootItems(queryData);
  const draggingVisualIndex = itemsToRender.findIndex(
    (entry) =>
      draggingCardId === entry.id.toString() ||
      draggingGroupId === entry.id.toString()
  );

  // Renders a card based on its type with common props
  const renderCard = (
    card: DbVoucher,
    _originalIndex: number,
    groupVisualIndex: number | null
  ) => {
    // VENDOR attachments: voucher response
    const response = voucherResponses?.find(
      (resp) => resp.voucher_id === card.id
    );

    // only latest vendor attachment serial_number
    let vendorFilesForCard: any[] | undefined = undefined;
    if (response?.attachments && response.attachments.length > 0) {
      const latestAttachment = response.attachments.reduce(
        (latest, current) => {
          if (!latest) return current;
          const latestSerial = latest.serial_number ?? 0;
          const currentSerial = current.serial_number ?? 0;
          return currentSerial > latestSerial ? current : latest;
        },
        null as any
      );
      vendorFilesForCard = latestAttachment ? [latestAttachment] : [];
    }

    const isDragging = draggingCardId === card.id.toString();

    const commonProps = {
      id: card.id.toString(),
      type: 'card' as const,
      subType: card.voucher_type,

      // TODO Optional Voucher API:
      isOptional: card.is_optional ?? false,

      isDeleted: card.state === 9,
      contractDeleted: !!contractDeleted,
      cardTitle: card.name,
      cardSubtitle: card.description ?? '',
      cardQuantity: card.unit_count ?? 0,
      cardFiles: card.attachments ?? [], // project-side attachments
      vendorFiles: vendorFilesForCard ?? [],
      unitType: card.unit_type,
      unitPrice: '',
      cardSum: '',
      responseData: response || null,
      cardCreationDate: card.created_at,
      sort: card.sort ?? 0,
      tenderVoucherId: card.tender_voucher_id,
      state: card.state,
      isDragging: isDragging,
      groupIndex: groupVisualIndex,
      isOnMyTender: isOnMyTender,
      tenderClosed: tenderClosed,
      isOfferLocked: isOfferLocked,
      contractDataMyTender: {
        shareId: shareId!,
        shareKey: shareKey!,
        contractOfferData: contractOfferData!,
      },
      contractData: {
        projectId: projectId,
        tenderId: tenderId,
        contractId: contractId,
      },
      setDraggingCardId,
      onContextMenu: (e: React.MouseEvent) =>
        handleRightClickCard(e, card.id, groupVisualIndex),
      onRightClick: (e: React.MouseEvent) =>
        handleRightClickCard(e, card.id, groupVisualIndex),
      onCardAction: (action: string) => {
        if (action === 'delete')
          handleCardContextMenuClick({ key: 'delete-card', modal: 'delete' });
        else if (action === 'reactivate-card')
          handleCardContextMenuClick({
            key: 1,
            icon: 'repeat',
            iconStyle: 'far',
            name: 'reactivate',
            modal: 'reactivate-card',
            minPermissionLevel: 0,
          });
        else if (action === 'edit-card')
          handleCardContextMenuClick({ key: 'edit-card', modal: 'edit-card' });
      },
    };

    switch (card.voucher_type) {
      case 1:
        return <UnitSumCard key={card.id} {...commonProps} />;
      case 2:
        return <OfferListCard key={card.id} {...commonProps} />;
      case 3:
        return <UploadCertifiedDocumentCard key={card.id} {...commonProps} />;
      case 4:
        return <TermOfServiceCard key={card.id} {...commonProps} />;
      default:
        return null;
    }
  };

  // Handles cancellation of delete operations
  const handleCancelDelete = () => {
    setItemToDelete(null);
    setShowConfirmDeleteCardModal(false);
    setShowConfirmDeleteGroupModal(false);
  };

  // Handles confirmation of card deletion
  const handleConfirmDeleteCard = async () => {
    if (itemToDelete?.type !== 'card') return;

    const { id: voucherId } = itemToDelete;

    if (!voucherId || !projectId || !tenderId || !contractId) {
      message.error(intl.formatMessage({ id: 'missing-required-ids' }));
      setShowConfirmDeleteCardModal(false);
      setItemToDelete(null);
      return;
    }
    try {
      await doDeleteVoucher({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        contractId: Number(contractId),
        voucherId: voucherId,
      });
    } catch (error) {
      console.error('Error in handleConfirmDeleteCard:', error);
    }
  };

  // Handles confirmation of group deletion
  const handleConfirmDeleteGroup = async () => {
    if (itemToDelete?.type !== 'group') return;

    const { id: voucherId } = itemToDelete;

    if (!voucherId || !projectId || !tenderId || !contractId) {
      message.error(intl.formatMessage({ id: 'missing-required-ids' }));
      setShowConfirmDeleteGroupModal(false);
      setItemToDelete(null);
      return;
    }

    // Double-check if group is empty
    const currentData = getCurrentData() ?? [];
    const group = currentData.find(
      (g) => g.id === voucherId && g.is_voucher_group
    );
    if (group && group.voucher_group_option?.sub_voucher_ids?.length > 0) {
      message.error('Cannot delete group: Group is not empty.');
      setShowConfirmDeleteGroupModal(false);
      setItemToDelete(null);
      return;
    }
    try {
      await doDeleteGroup({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        contractId: Number(contractId),
        voucherId: voucherId,
      });
    } catch (error) {
      console.error('Error in handleConfirmDeleteGroup:', error);
    }
  };

  // console.log(
  //   `TUS: Contract Builder -> Voucher's Attachments: `,
  //   itemsToRender
  // );

  if (isLoading)
    return <Spin tip={intl.formatMessage({ id: 'loading-contract' })} />;

  return (
    <Fragment>
      {/* Header Buttons */}
      <Row>
        <Col span={24}>
          <div css={styles.contractBuilderUpperHeader}>
            <span style={{ fontSize: '13px' }}>
              {itemsToRender &&
                itemsToRender.length > 0 &&
                !isOnMyTender &&
                intl.formatMessage({ id: 'drag-and-drop-to-reorder' })}
            </span>

            {!isOnMyTender && (
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '20px',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  justifySelf: 'flex-end',
                }}>
                <Button
                  type="text"
                  css={styles.btnCenter}
                  disabled={!!tenderClosed || !!contractDeleted}
                  onClick={() => {
                    if (tenderClosed || contractDeleted) return;
                    setShowAddGroupModal(true);
                  }}>
                  <IconButton
                    color={tenderClosed || contractDeleted ? 'grey' : 'blue'}
                    backgroundOff={true}
                    icon={['far', 'square-dashed-circle-plus']}
                    customStyle={{ fontSize: '22px' }}
                  />
                  {intl.formatMessage({ id: 'add-group' })}
                </Button>
                <Button
                  type="text"
                  css={styles.btnCenter}
                  disabled={!!tenderClosed || !!contractDeleted}
                  onClick={() => {
                    if (tenderClosed || contractDeleted) return;
                    setRightClickGroupIndex(null);
                    setShowAddCardModal(true);
                  }}>
                  <IconButton
                    color={tenderClosed || contractDeleted ? 'grey' : 'blue'}
                    backgroundOff={true}
                    icon={['far', 'square-plus']}
                    customStyle={{ fontSize: '22px' }}
                  />
                  {intl.formatMessage({ id: 'add-voucher' })}
                </Button>
              </div>
            )}
          </div>
          {/* Main Content Area */}
          <div css={styles.contractBuilderLower}>
            {itemsToRender && itemsToRender.length > 0
              ? itemsToRender.map((entry, visualIndex) => {
                  // spacer out - render logic
                  const originalIndex = (queryData ?? []).findIndex(
                    (item) => item.id === entry.id
                  );
                  const isDraggingSelf =
                    draggingCardId === entry.id.toString() ||
                    draggingGroupId === entry.id.toString();
                  const isSpacerAfterDragged =
                    draggingVisualIndex !== -1 &&
                    draggingVisualIndex === visualIndex - 1;
                  return (
                    <Fragment key={entry.id}>
                      {/* Spacer */}
                      {(draggingCardId || draggingGroupId) &&
                        !isDraggingSelf &&
                        !isSpacerAfterDragged && (
                          <UniversalDropSpacer
                            index={visualIndex}
                            groupIndex={null}
                            onDropCard={handleCardDrop}
                            onDropGroup={handleGroupDrop}
                            active
                            draggingCardId={draggingCardId}
                            draggingGroupId={draggingGroupId}
                          />
                        )}
                      {/* Group or Card */}
                      {entry.is_voucher_group ? (
                        <ContractBuilderGroup
                          groupId={entry.id.toString()}
                          groupTitle={entry.name}
                          isDeleted={entry.state === 9}
                          contractDeleted={!!contractDeleted}
                          cards={(
                            entry.voucher_group_option?.sub_voucher_ids ?? []
                          )
                            .map((id) =>
                              (queryData ?? []).find(
                                (v) =>
                                  !v.is_voucher_group &&
                                  v.id.toString() === id.toString()
                              )
                            )
                            .filter((card): card is DbVoucher => Boolean(card))
                            .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))}
                          groupIndex={visualIndex}
                          dndIndex={visualIndex}
                          isOnMyTender={isOnMyTender}
                          tenderClosed={tenderClosed}
                          isDragging={draggingGroupId === entry.id.toString()}
                          setDraggingGroupId={setDraggingGroupId}
                          draggingGroupId={draggingGroupId}
                          onDropCardToGroup={handleDropCardToGroup}
                          onRightClick={(e) =>
                            handleRightClickCard(e, entry.id, visualIndex)
                          }
                          onItemClick={(item) =>
                            handleGroupContextMenuClick(item, originalIndex)
                          }
                          renderCard={(card) =>
                            renderCard(card, -1, visualIndex)
                          } // Pass group's visual index
                          draggingCardId={draggingCardId}
                          onReorderCardsInGroup={handleReorderCardsInGroup} // Use the correct handler
                        />
                      ) : (
                        renderCard(entry, originalIndex, null)
                      )}
                    </Fragment>
                  );
                })
              : intl.formatMessage({ id: 'no-vouchers-found' })}
            {/* Final Spacer */}
            {(draggingCardId || draggingGroupId) && (
              <UniversalDropSpacer
                index={itemsToRender.length}
                groupIndex={null}
                onDropCard={handleCardDrop}
                onDropGroup={handleGroupDrop}
                active
                draggingCardId={draggingCardId}
                draggingGroupId={draggingGroupId}
              />
            )}
          </div>
        </Col>

        {isOnMyTender && contractOfferData?.is_offer_created && (
          <div
            style={{
              marginTop: '20px',
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
            }}>
            <Button
              type="primary"
              size="large"
              css={{
                backgroundColor: 'green',
                borderColor: 'green',
                ':hover': {
                  backgroundColor: '#00aa00',
                  borderColor: '#00aa00',
                },
                ':disabled': {
                  backgroundColor: '#d9d9d9',
                  borderColor: '#d9d9d9',
                },
              }}
              disabled={
                !(
                  contractOfferData.state === 1 || contractOfferData.state === 3
                ) ||
                !isOfferFinalizable ||
                !!tenderClosed
              }
              loading={isReleasing}
              onClick={() => setShowConfirmFinalizeModal(true)}>
              {intl.formatMessage({ id: 'finalize-offer' })}
            </Button>
            <Button
              type="default"
              size="large"
              css={{
                backgroundColor: '#ff9800',
                borderColor: '#ff9800',
                color: 'white',
                ':hover': {
                  backgroundColor: '#e68900',
                  borderColor: '#e68900',
                  color: 'white',
                },
                ':disabled': {
                  backgroundColor: '#d9d9d9',
                  borderColor: '#d9d9d9',
                  color: 'rgba(0, 0, 0, 0.25)',
                },
              }}
              disabled={
                !(
                  contractOfferData.state === 4 || contractOfferData.state === 5
                ) || !!tenderClosed
              }
              loading={isReopening}
              onClick={() => setShowConfirmReopenModal(true)}>
              {intl.formatMessage({ id: 'offer-reopen' })}
            </Button>
          </div>
        )}
      </Row>
      {/* Modals */}
      <ContractBuilderAddGroupModal
        visible={showAddGroupModal}
        onCancel={() => setShowAddGroupModal(false)}
        okButtonProps={{
          disabled: !!tenderClosed,
        }}
        onOk={handleAddGroup}
        confirmLoading={isAddingGroup}
      />
      <ContractBuilderAddCardModal
        visible={showAddCardModal}
        onCancel={() => setShowAddCardModal(false)}
        onOk={handleAddCard}
        confirmLoading={isAddingCard}
        tenderClosed={tenderClosed}
      />
      {/* Delete Card Confirmation Modal */}
      {showConfirmDeleteCardModal && itemToDelete?.type === 'card' && (
        <SimpleConfirmModal
          visible={showConfirmDeleteCardModal}
          isLoading={isDeletingCard}
          okButtonProps={{
            disabled: !!tenderClosed,
          }}
          onOk={handleConfirmDeleteCard}
          onCancel={handleCancelDelete}
          modalTitle={`${intl.formatMessage({ id: 'delete-card' })} - ${
            itemToDelete?.name
          }`}
          modalIcon={['far', 'trash']}
          okText={intl.formatMessage({ id: 'delete' })}
          okType="danger"
        />
      )}
      {/* Delete Group Confirmation Modal */}
      {showConfirmDeleteGroupModal && itemToDelete?.type === 'group' && (
        <SimpleConfirmModal
          visible={showConfirmDeleteGroupModal}
          isLoading={isDeletingGroup}
          okButtonProps={{
            disabled: !!tenderClosed,
          }}
          onOk={handleConfirmDeleteGroup}
          onCancel={handleCancelDelete}
          modalTitle={`${intl.formatMessage({ id: 'delete-group' })} - ${
            itemToDelete?.name
          }`}
          modalIcon={['far', 'trash']}
          okText={intl.formatMessage({ id: 'delete' })}
          okType="danger"
        />
      )}
      {/* Edit Voucher Modal */}
      {itemToEdit && (
        <ContractBuilderEditModal
          projectId={projectId}
          tenderId={tenderId}
          contractId={contractId}
          voucherId={itemToEdit.data.tender_voucher_id}
          previousVoucherData={itemToEdit}
          visible={!!itemToEdit}
          onCancel={() => setItemToEdit(null)}
          okButtonProps={{
            disabled: !!tenderClosed,
          }}
          onOk={() => setItemToEdit(null)}
        />
      )}
      {/* Finalize Offer Confirmation Modal */}
      {showConfirmFinalizeModal && (
        <SimpleConfirmModal
          visible={showConfirmFinalizeModal}
          isLoading={isReleasing}
          modalTitle={intl.formatMessage({
            id: 'confirm-finalize-offer-title',
          })}
          modalIcon={['far', 'check-circle']}
          onCancel={() => setShowConfirmFinalizeModal(false)}
          onOk={() => {
            if (
              contractOfferData.state !== 1 &&
              contractOfferData.state !== 3
            ) {
              message.error(
                intl.formatMessage({ id: 'offer-cannot-finalize' })
              );
              setShowConfirmFinalizeModal(false);
              return;
            }
            doReleaseOffer({
              share_id: shareId!,
              share_key: shareKey!,
              offer_id: contractOfferData.offer_id,
              action: 'accept',
            });
          }}
          okText={intl.formatMessage({ id: 'finalize-offer' })}
          okType="primary"
          destroyOnClose>
          <Typography.Paragraph style={{ padding: '20px' }}>
            {intl.formatMessage({ id: 'confirm-finalize-offer-message' })}
          </Typography.Paragraph>
        </SimpleConfirmModal>
      )}
      {/* Reopen Offer Confirmation Modal */}
      {showConfirmReopenModal && (
        <SimpleConfirmModal
          visible={showConfirmReopenModal}
          isLoading={isReopening}
          modalTitle={intl.formatMessage({ id: 'confirm-reopen-offer-title' })}
          modalIcon={['far', 'redo']}
          okButtonProps={{
            style: {
              backgroundColor: '#ff9800',
              borderColor: '#ff9800',
              color: 'white',
            },
          }}
          onCancel={() => setShowConfirmReopenModal(false)}
          onOk={() => {
            if (
              contractOfferData.state !== 4 &&
              contractOfferData.state !== 5
            ) {
              message.error(intl.formatMessage({ id: 'offer-cannot-reopen' }));
              setShowConfirmReopenModal(false);
              return;
            }
            doReopenOffer({
              share_id: shareId!,
              share_key: shareKey!,
              offer_id: contractOfferData.offer_id,
              action: 'reopen',
            });
          }}
          okText={intl.formatMessage({ id: 'offer-reopen' })}
          okType="default"
          destroyOnClose>
          <Typography.Paragraph style={{ padding: '20px' }}>
            {intl.formatMessage({ id: 'confirm-reopen-offer-message' })}
          </Typography.Paragraph>
        </SimpleConfirmModal>
      )}
    </Fragment>
  );
};

export default ContractBuilder;
